import React, { useState, useEffect } from 'react';

import axios from 'axios';

import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

import { TARIF_TYPES, TERRITOIRES, TYPES_ENTREPRISES } from '../GestionTarifsDemandesPubliques.constants';

import { buildTarifsConfig } from './buildTarifsConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function useGestionTarifs() {
  const { getAuthHeader } = useAuth();
  
  // États principaux
  const [activeTab, setActiveTab] = useState('tarifs');
  const [tarifs, setTarifs] = useState([]);
  const [tarifsParTerritoire, setTarifsParTerritoire] = useState([]);
  const [tarifsParType, setTarifsParType] = useState([]);
  const [tarifsParEntreprise, setTarifsParEntreprise] = useState([]);
  
  // Configurations par type de demande PAR ESPACE (member/cooper/expert/default)
  const SPACES_TARIF = ['default', 'member', 'cooper', 'expert'];
  const emptyTypeConfig = () => ({ prix_cb: 15, credits: 5, coef_urgence: 1.5 });
  const [configParTypeBySpace, setConfigParTypeBySpace] = useState(
    SPACES_TARIF.reduce((acc, sp) => ({
      ...acc,
      [sp]: TARIF_TYPES.reduce((typesAcc, type) => ({
        ...typesAcc,
        [type.value]: emptyTypeConfig(),
      }), {}),
    }), {})
  );
  const [activeSpaceTarif, setActiveSpaceTarif] = useState('default');
  const [dirtyTypeBySpace, setDirtyTypeBySpace] = useState({});

  // Legacy compat: configParType pointe vers l'espace actif
  const configParType = configParTypeBySpace[activeSpaceTarif] || {};
  const [configParEntreprise, setConfigParEntreprise] = useState(
    TYPES_ENTREPRISES.reduce((acc, ent) => ({
      ...acc,
      [ent.value]: { coefficient: 1.0, remise: 0 }
    }), {})
  );
  
  // Configuration par TERRITOIRE
  const [configParTerritoire, setConfigParTerritoire] = useState(
    TERRITOIRES.reduce((acc, terr) => ({
      ...acc,
      [terr.value]: { coefficient: terr.value === 'metropole' ? 1.0 : 0.8, remise_speciale: 0, enabled: true, disable_coef_remise: false }
    }), {})
  );
  
  const [demandes, setDemandes] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTerritoire, setSelectedTerritoire] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedStatut, setSelectedStatut] = useState('all');
  
  // Dialogs
  const [tarifDialogOpen, setTarifDialogOpen] = useState(false);
  const [editingTarif, setEditingTarif] = useState(null);
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  
  // Formulaire tarif
  const [tarifForm, setTarifForm] = useState({
    nom: '',
    code: '',
    type_demande: 'standard',
    territoire: 'metropole',
    type_entreprise: 'all',
    prix_base: 0,
    prix_credits: 0,
    tva_applicable: true,
    coefficient_urgence: 1.5,
    coefficient_premium: 2.0,
    remise_volume: 0,
    remise_fidelite: 0,
    is_active: true,
    description: '',
    // Territoires et types d'entreprise désactivés pour ce tarif
    territoires_disabled: [],
    types_entreprise_disabled: [],
    // Taxes personnalisées (affichage exclusif sur facture/reçu)
    taxes_personnalisees: [],
  });

  // V36.41 — Listes dynamiques chargees depuis l'API (fallback : constantes)
  const [territoiresList, setTerritoiresList] = useState(TERRITOIRES);
  const [typesEntrepriseList, setTypesEntrepriseList] = useState(TYPES_ENTREPRISES);
  const [typesDemandeList, setTypesDemandeList] = useState(TARIF_TYPES);
  // V36.41 — Politique de remises Communityplace (editable)
  const [remisesPolitique, setRemisesPolitique] = useState({
    delai_grace_jours: 7,
    penalite_credits_par_jour: 15,
    max_remises_par_cooper_30j: 3,
    description: '',
  });
  const [remisesPolitiqueLoading, setRemisesPolitiqueLoading] = useState(false);

  useEffect(() => {
    fetchAllData();
    loadReferentielsFromApi();
    loadRemisesPolitique();
  }, []);

  // Charge les referentiels (territoires, types entreprise, types demande)
  // depuis l'API; convertit en format `{value, label, description, meta}` compatible avec l'existant.
  const loadReferentielsFromApi = async () => {
    try {
      const [terrRes, entRes, typRes] = await Promise.all([
        axios.get(`${API}/admin/referentiels/territoires`, { headers: getAuthHeader() }).catch(() => null),
        axios.get(`${API}/admin/referentiels/types-entreprise`, { headers: getAuthHeader() }).catch(() => null),
        axios.get(`${API}/admin/referentiels/types-demande`, { headers: getAuthHeader() }).catch(() => null),
      ]);
      const toLegacy = (apiItems, defaults) => {
        if (!apiItems || apiItems.length === 0) return defaults;
        return apiItems
          .filter((it) => it.is_active !== false)
          .map((it) => ({
            value: it.value,
            label: it.label,
            description: it.description || '',
            tva: it.meta?.tva,
            icon: it.meta?.icon,
            color: it.meta?.color,
          }));
      };
      if (terrRes?.data?.items) setTerritoiresList(toLegacy(terrRes.data.items, TERRITOIRES));
      if (entRes?.data?.items) setTypesEntrepriseList(toLegacy(entRes.data.items, TYPES_ENTREPRISES));
      if (typRes?.data?.items) setTypesDemandeList(toLegacy(typRes.data.items, TARIF_TYPES));
    } catch {
      // Fallback silencieux : on garde les constantes par defaut
    }
  };

  const loadRemisesPolitique = async () => {
    setRemisesPolitiqueLoading(true);
    try {
      const res = await axios.get(`${API}/admin/referentiels/remises-politique/config`, { headers: getAuthHeader() });
      if (res?.data) setRemisesPolitique(res.data);
    } catch {
      // OK, on garde defaults
    } finally {
      setRemisesPolitiqueLoading(false);
    }
  };

  const saveRemisesPolitique = async () => {
    try {
      await axios.put(`${API}/admin/referentiels/remises-politique/config`, remisesPolitique, { headers: getAuthHeader() });
      toast.success('Politique de remises mise a jour');
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  // V36.41b — Modale de visualisation pro d'une demande publique
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingDemande, setViewingDemande] = useState(null);

  const handleViewDemande = (demande) => {
    setViewingDemande(demande);
    setViewDialogOpen(true);
  };

  // V36.41 — CRUD Demandes Publiques (admin)
  const [demandeDialogOpen, setDemandeDialogOpen] = useState(false);
  const [editingDemande, setEditingDemande] = useState(null);
  const [demandeForm, setDemandeForm] = useState({
    title: '', description: '', secteur: 'general', territoire: 'metropole',
    type_entreprise: 'all', type_demande: 'standard', ville: '', notes: '',
    // V37.26 — Identification entreprise + contact demandeur
    raison_sociale: '', siret: '',
    nom: '', prenom: '', email: '', telephone: '',
    adresse: '', code_postal: '',
    // V37.27 — Activités professionnelles (multi-select)
    activites: [],
    // V37.29 — Mode de publication : 'immediat' | 'differe' | 'brouillon'
    publication_mode: 'immediat',
    publication_scheduled_at: '',
  });

  // V37.27 — Helpers parse/serialize activites (compat backend `secteur`)
  const parseActivitesFromDemande = (demande) => {
    if (Array.isArray(demande?.activites) && demande.activites.length > 0) {
      return demande.activites.filter(Boolean);
    }
    const raw = (demande?.secteur || '').trim();
    if (!raw || raw === 'general') return [];
    return raw.split(/[,;|]/).map(s => s.trim().toLowerCase()).filter(Boolean);
  };

  const toggleActivite = (value) => {
    setDemandeForm((prev) => {
      const set = new Set(prev.activites || []);
      if (set.has(value)) set.delete(value); else set.add(value);
      return { ...prev, activites: Array.from(set) };
    });
  };

  const handleAdminEditDemande = (demande) => {
    setEditingDemande(demande);
    setDemandeForm({
      title: demande.title || demande.titre || '',
      description: demande.description || demande.message || '',
      secteur: demande.secteur || 'general',
      territoire: demande.territoire || 'metropole',
      type_entreprise: demande.type_entreprise || 'all',
      type_demande: demande.type_demande || 'standard',
      ville: demande.ville || '',
      notes: demande.notes || '',
      statut: demande.statut || 'nouvelle',
      // V37.26
      raison_sociale: demande.raison_sociale || demande.entreprise || '',
      siret: demande.siret || '',
      nom: demande.nom || '',
      prenom: demande.prenom || '',
      email: demande.email || '',
      telephone: demande.telephone || '',
      adresse: demande.adresse || '',
      code_postal: demande.code_postal || '',
      // V37.27
      activites: parseActivitesFromDemande(demande),
      // V37.29 — Re-déterminer le mode à partir du statut
      publication_mode: demande.statut === 'brouillon'
        ? 'brouillon'
        : (demande.statut === 'programmee' || demande.publication_scheduled_at)
          ? 'differe'
          : 'immediat',
      publication_scheduled_at: demande.publication_scheduled_at
        ? String(demande.publication_scheduled_at).slice(0, 16)
        : '',
    });
    setDemandeDialogOpen(true);
  };

  // V37.30 — Vue « Demandes programmees & brouillons »
  const [programmees, setProgrammees] = useState([]);
  const [programmeesLoading, setProgrammeesLoading] = useState(false);
  const [tickNow, setTickNow] = useState(Date.now());

  // Tick chaque seconde pour mettre a jour les comptes a rebours
  useEffect(() => {
    if (!Array.isArray(programmees) || programmees.length === 0) return undefined;
    const hasScheduled = programmees.some(d => d.statut === 'programmee');
    if (!hasScheduled) return undefined;
    const interval = setInterval(() => setTickNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [programmees]);

  const fetchProgrammees = async () => {
    setProgrammeesLoading(true);
    try {
      const res = await axios.get(`${API}/demandes-publiques/admin/programmees`, { headers: getAuthHeader() });
      setProgrammees(res.data?.items || []);
    } catch {
      // silent
    } finally {
      setProgrammeesLoading(false);
    }
  };

  useEffect(() => { fetchProgrammees(); }, []);

  const handlePublishNow = async (demande) => {
    if (!window.confirm(`Publier immédiatement "${demande.title || 'cette demande'}" ?`)) return;
    try {
      await axios.post(`${API}/demandes-publiques/${demande.id}/publish-now`, {}, { headers: getAuthHeader() });
      toast.success('Demande publiée immédiatement sur la Communityplace');
      fetchProgrammees();
      fetchDemandes();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const handleCancelSchedule = async (demande) => {
    if (!window.confirm(`Annuler la programmation de "${demande.title || 'cette demande'}" ? Elle repasse en brouillon.`)) return;
    try {
      await axios.post(`${API}/demandes-publiques/${demande.id}/cancel-schedule`, {}, { headers: getAuthHeader() });
      toast.success('Programmation annulée — demande en brouillon');
      fetchProgrammees();
      fetchDemandes();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  // Formate le compte a rebours jusqu'a une date ISO
  const formatCountdown = (isoDate, now = tickNow) => {
    if (!isoDate) return '';
    const target = new Date(isoDate).getTime();
    const diff = target - now;
    if (diff <= 0) return 'Publication imminente…';
    const sec = Math.floor(diff / 1000);
    const d = Math.floor(sec / 86400);
    const h = Math.floor((sec % 86400) / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    if (d > 0) return `${d}j ${h}h ${m}m`;
    if (h > 0) return `${h}h ${m}m ${s}s`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  };

  const handleAdminDuplicateDemande = async (demande) => {
    try {
      await axios.post(`${API}/demandes-publiques/${demande.id}/duplicate`, {}, { headers: getAuthHeader() });
      toast.success(`"${demande.title || demande.titre}" dupliquee`);
      fetchDemandes();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const handleAdminDeleteDemande = async (demande) => {
    if (!window.confirm(`Supprimer DEFINITIVEMENT "${demande.title || demande.titre}" ?`)) return;
    try {
      await axios.delete(`${API}/demandes-publiques/${demande.id}/admin-delete`, { headers: getAuthHeader() });
      toast.success('Demande supprimee');
      fetchDemandes();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const handleSaveAdminDemande = async () => {
    if (!demandeForm.title?.trim()) {
      toast.error('Le titre est requis');
      return;
    }
    // V37.29 — Validation du mode différé
    if (demandeForm.publication_mode === 'differe') {
      if (!demandeForm.publication_scheduled_at) {
        toast.error('Veuillez choisir une date de publication pour le mode différé');
        return;
      }
      if (new Date(demandeForm.publication_scheduled_at) <= new Date()) {
        toast.error('La date de publication différée doit être dans le futur');
        return;
      }
    }
    try {
      // V37.27 — serialisation des activites en string + tableau pour le backend
      const activites = Array.isArray(demandeForm.activites) ? demandeForm.activites : [];
      const payload = {
        ...demandeForm,
        activites,
        secteur: activites.length > 0 ? activites.join(', ') : (demandeForm.secteur || 'general'),
        // V37.29 — Nettoyer le scheduled_at si pas en mode différé
        publication_scheduled_at: demandeForm.publication_mode === 'differe'
          ? new Date(demandeForm.publication_scheduled_at).toISOString()
          : null,
      };
      if (editingDemande) {
        await axios.put(`${API}/demandes-publiques/${editingDemande.id}/admin-edit`, payload, { headers: getAuthHeader() });
        toast.success('Demande modifiee');
      } else {
        await axios.post(`${API}/demandes-publiques/admin-create`, payload, { headers: getAuthHeader() });
        const successMsg = {
          immediat: 'Demande publiée immédiatement sur la Communityplace',
          differe: 'Demande programmée — publication différée',
          brouillon: 'Demande sauvegardée en brouillon',
        }[demandeForm.publication_mode] || 'Demande creee';
        toast.success(successMsg);
      }
      setDemandeDialogOpen(false);
      fetchDemandes();
      fetchProgrammees();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchTarifs(),
        fetchDemandes(),
        fetchStats(),
        fetchConfigTerritoires(),
        fetchConfigTypesAllSpaces(),
      ]);
    } catch {
    } finally {
      setLoading(false);
    }
  };

  // Charge les configs par type pour tous les espaces (matrix view)

  const _cfg = buildTarifsConfig({ tarifs, configParTypeBySpace, activeSpaceTarif, configParEntreprise, configParTerritoire, demandes, stats, searchTerm, selectedTerritoire, selectedType, selectedStatut, editingTarif, tarifForm, setTarifs, setTarifsParTerritoire, setTarifsParType, setTarifsParEntreprise, setConfigParTypeBySpace, setDirtyTypeBySpace, setConfigParEntreprise, setConfigParTerritoire, setDemandes, setStats, setTarifDialogOpen, setEditingTarif, setTarifForm, SPACES_TARIF, getAuthHeader, emptyTypeConfig });
  const { fetchConfigTypesAllSpaces, fetchConfigTerritoires, updateConfigTerritoire, handleSaveConfigTerritoire, fetchTarifs, fetchDemandes, fetchStats, handleSaveTarif, handleDeleteTarif, handleSaveConfigType, handleSaveAllConfigTypeSpace, handleCloneFromDefault, handleSaveConfigEntreprise, updateConfigType, updateConfigEntreprise, handleToggleTarif, handleDuplicateTarif, openEditDialog, resetTarifForm, getTypeInfo, getTerritoireInfo, filteredDemandes, handleExport } = _cfg;

  const ctx = {
    activeTab, tarifs, tarifsParTerritoire, tarifsParType, tarifsParEntreprise,
    configParTypeBySpace, activeSpaceTarif, dirtyTypeBySpace, configParEntreprise,
    configParTerritoire, demandes, stats, loading, searchTerm, selectedTerritoire,
    selectedType, selectedStatut, tarifDialogOpen, editingTarif, statsDialogOpen,
    tarifForm, territoiresList, typesEntrepriseList, typesDemandeList, remisesPolitique,
    remisesPolitiqueLoading, viewDialogOpen, viewingDemande, demandeDialogOpen,
    editingDemande, demandeForm, programmees, programmeesLoading, tickNow,
    setActiveTab, setTarifs, setTarifsParTerritoire, setTarifsParType, setTarifsParEntreprise,
    setConfigParTypeBySpace, setActiveSpaceTarif, setDirtyTypeBySpace, setConfigParEntreprise,
    setConfigParTerritoire, setDemandes, setStats, setLoading, setSearchTerm, setSelectedTerritoire,
    setSelectedType, setSelectedStatut, setTarifDialogOpen, setEditingTarif, setStatsDialogOpen,
    setTarifForm, setTerritoiresList, setTypesEntrepriseList, setTypesDemandeList, setRemisesPolitique,
    setRemisesPolitiqueLoading, setViewDialogOpen, setViewingDemande, setDemandeDialogOpen,
    setEditingDemande, setDemandeForm, setProgrammees, setProgrammeesLoading, setTickNow,
    fetchAllData, fetchConfigTerritoires, fetchConfigTypesAllSpaces, fetchDemandes, fetchProgrammees,
    fetchStats, fetchTarifs, filteredDemandes, formatCountdown, getTerritoireInfo, getTypeInfo,
    handleAdminDeleteDemande, handleAdminDuplicateDemande, handleAdminEditDemande, handleCancelSchedule,
    handleCloneFromDefault, handleDeleteTarif, handleDuplicateTarif, handleExport, handlePublishNow,
    handleSaveAdminDemande, handleSaveAllConfigTypeSpace, handleSaveConfigEntreprise,
    handleSaveConfigTerritoire, handleSaveConfigType, handleSaveTarif, handleToggleTarif, handleViewDemande,
    loadReferentielsFromApi, loadRemisesPolitique, openEditDialog, parseActivitesFromDemande,
    resetTarifForm, saveRemisesPolitique, toggleActivite, updateConfigEntreprise, updateConfigTerritoire,
    updateConfigType, SPACES_TARIF, getAuthHeader, emptyTypeConfig,
  };

  return ctx;
}
